import { useEffect, useState } from "react";
import { ChevronDown, Plus, Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SerialScanButton } from "@/components/SerialScanner";
import { toast } from "sonner";

const norm = (s) => (s || "").trim().toLowerCase();
// Riconosce il codice letto: barcode o codice articolo, altrimenti seriale presente nel furgone
const resolveScan = (code, catalog, devices = []) => {
  const c = norm(code);
  const item = catalog.find((i) => i.serialized && (norm(i.barcode) === c || norm(i.code) === c));
  if (item) return { item_id: item.id, label: `${item.code} · ${item.name}` };
  const st = devices.find((s) => s.serials.some((x) => norm(x) === c));
  if (st) return { item_id: st.item_id, serial: st.serials.find((x) => norm(x) === c), label: `${st.code} · ${st.name}` };
  toast.error(`Codice ${code} non riconosciuto: scegli l'apparato dall'elenco`);
  return null;
};
const ItemScan = ({ catalog, devices, onFound, testId }) => (
  <SerialScanButton title="Scansiona barcode apparato" testId={testId} onDetected={(code) => {
    const r = resolveScan(code, catalog, devices);
    if (r) { onFound(r); toast.success(`Apparato riconosciuto: ${r.label}`); }
  }} />
);

const FREE = "__free__";

const useVan = (teamId) => {
  const [data, setData] = useState({ vans: [], catalog: [], frequent: [] });
  useEffect(() => { api.get("/tech/van").then((r) => setData(r.data)).catch(() => {}); }, []);
  const van = data.vans.find((v) => v.team_id === teamId) || data.vans[0];
  return { van, catalog: data.catalog, frequent: data.frequent || [] };
};

const match = (q, c) => !q || `${c.code} ${c.name}`.toLowerCase().includes(q.toLowerCase());
const Opt = ({ c, extra, onPick, testId }) => (
  <button type="button" onClick={() => onPick(c.id || c.item_id)} className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-2.5 text-left text-sm hover:bg-accent" data-testid={testId}>
    <span><span className="font-mono text-xs">{c.code}</span> · {c.name}</span>{extra && <span className="shrink-0 text-xs text-muted-foreground">{extra}</span>}
  </button>
);
const Group = ({ title, children }) => <div className="py-1"><div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>{children}</div>;

const DeviceSelect = ({ value, devices, title, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const cur = devices.find((s) => s.item_id === value);
  const shown = devices.filter((s) => match(q, s));
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-11 w-full items-center justify-between border bg-white px-2 text-left text-sm" data-testid={testId}>
          <span className={`truncate ${cur ? "" : "text-muted-foreground"}`}>{cur ? `${cur.code} · ${cur.name}` : "Seleziona apparato…"}</span><ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0" data-testid={`${testId}-options`}>
        <div className="flex items-center border-b px-2"><Search className="h-4 w-4 opacity-50" />
          <input autoFocus className="h-11 w-full px-2 text-base outline-none" placeholder="Cerca nome o codice apparato…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search`} />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {shown.length > 0 && <Group title={title}>{shown.map((s) => <Opt key={s.item_id} c={s} extra={s.extra} onPick={pick} testId={`${testId}-opt-${s.code.replace(/\s+/g, "_")}`} />)}</Group>}
          {!shown.length && <p className="px-2 py-2 text-xs text-muted-foreground" data-testid={`${testId}-empty`}>Nessun apparato trovato.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ItemSelect = ({ value, van, catalog, frequent, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const byId = Object.fromEntries(catalog.map((c) => [c.id, c]));
  const stock = (van?.stock || []).filter((s) => byId[s.item_id] && !byId[s.item_id].serialized);
  const qty = Object.fromEntries(stock.map((s) => [s.item_id, s.qty > 0 ? `${s.qty} ${s.unit}` : "esaurito"]));
  const fav = frequent.map((id) => byId[id]).filter((c) => c && !c.serialized && match(q, c));
  const shown = new Set([...fav.map((c) => c.id), ...stock.map((s) => s.item_id)]);
  const others = q.length >= 2 ? catalog.filter((c) => !c.serialized && !c.category?.startsWith("Attrezzatura") && !shown.has(c.id) && match(q, c)).slice(0, 50) : [];
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };
  const cur = value === FREE ? "Altro materiale (testo libero)" : byId[value] ? `${byId[value].code} · ${byId[value].name}` : "";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-11 w-full items-center justify-between border bg-white px-2 text-left text-sm" data-testid={testId}>
          <span className={`truncate ${cur ? "" : "text-muted-foreground"}`}>{cur || "Seleziona materiale…"}</span><ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0" data-testid={`${testId}-options`}>
        <div className="flex items-center border-b px-2"><Search className="h-4 w-4 opacity-50" />
          <input autoFocus className="h-11 w-full px-2 text-base outline-none" placeholder="Cerca codice o descrizione…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search`} />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {fav.length > 0 && <Group title="I tuoi più usati">{fav.map((c) => <Opt key={c.id} c={c} extra={qty[c.id]} onPick={pick} testId={`${testId}-fav-${c.code.replace(/\s+/g, "_")}`} />)}</Group>}
          {stock.some((s) => !fav.find((c) => c.id === s.item_id) && match(q, s)) && <Group title={`Nel ${van.name}`}>{stock.filter((s) => !fav.find((c) => c.id === s.item_id) && match(q, s)).map((s) => <Opt key={s.item_id} c={s} extra={qty[s.item_id]} onPick={pick} testId={`${testId}-van-${s.code.replace(/\s+/g, "_")}`} />)}</Group>}
          {others.length > 0 && <Group title="Altri articoli a catalogo">{others.map((c) => <Opt key={c.id} c={c} onPick={pick} testId={`${testId}-cat-${c.code.replace(/\s+/g, "_")}`} />)}</Group>}
          {q.length < 2 && <p className="px-2 py-2 text-xs text-muted-foreground">Scrivi almeno 2 lettere per cercare in tutto il catalogo.</p>}
          {q.length >= 2 && !fav.length && !others.length && !stock.some((s) => match(q, s)) && <p className="px-2 py-2 text-xs text-muted-foreground" data-testid={`${testId}-empty`}>Nessun materiale trovato.</p>}
          <button type="button" onClick={() => pick(FREE)} className="mt-1 w-full border-t px-2 py-2.5 text-left text-sm font-semibold" data-testid={`${testId}-free`}>Altro materiale (testo libero)</button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Row = ({ m, i, van, catalog, frequent, update, remove }) => {
  const item = catalog.find((c) => c.id === m.item_id);
  const stock = van?.stock.find((s) => s.item_id === m.item_id);
  const listId = `van-serials-${i}`;
  const serialKnown = !m.serial || stock?.serials.includes(m.serial);
  return (
    <div className="mb-3 border-b pb-3 last:border-b-0" data-testid={`tech-material-row-${i}`}>
      <div className="flex gap-2">
        <ItemSelect value={m.free ? FREE : m.item_id || ""} van={van} catalog={catalog} frequent={frequent} testId={`tech-material-item-${i}`}
          onChange={(v) => update({ item_id: v === FREE ? null : v || null, free: v === FREE, name: v === FREE ? "" : catalog.find((c) => c.id === v)?.name || "", serial: "", qty: 1 })} />
        <Button size="icon" variant="ghost" className="h-11 shrink-0" onClick={remove} aria-label="Rimuovi" data-testid={`tech-material-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {m.free && <Input className="mt-2 h-11" placeholder="Descrizione materiale" value={m.name} onChange={(e) => update({ name: e.target.value })} data-testid={`tech-material-name-${i}`} />}
      {item?.serialized ? (
        <div className="mt-2">
          <div className="flex gap-2">
            <Input className="h-11 font-mono" list={listId} placeholder="Numero di serie" value={m.serial} onChange={(e) => update({ serial: e.target.value.trim() })} data-testid={`tech-material-serial-${i}`} />
            <datalist id={listId}>{(stock?.serials || []).map((s) => <option key={s} value={s} />)}</datalist>
            <SerialScanButton onDetected={(code) => update({ serial: code })} testId={`tech-material-scan-${i}`} />
          </div>
          {!serialKnown && <p className="mt-1 text-xs text-amber-700" data-testid={`tech-material-serial-warning-${i}`}>Seriale non presente nel tuo furgone: verrà registrato e segnalato al magazzino.</p>}
        </div>
      ) : (m.item_id || m.free) && (
        <div className="mt-2 flex items-center gap-2">
          <Input className="h-11 w-28" type="number" min="0" step="any" value={m.qty} onChange={(e) => update({ qty: e.target.value })} data-testid={`tech-material-qty-${i}`} />
          <span className="text-sm text-muted-foreground">{item?.unit || "pz"}{stock && ` · nel furgone ${stock.qty}`}</span>
        </div>
      )}
      {item && !item.serialized && stock && Number(m.qty) > stock.qty && <p className="mt-1 text-xs text-amber-700" data-testid={`tech-material-stock-warning-${i}`}>Quantità superiore alla giacenza del furgone: il magazzino riceverà un avviso.</p>}
      {item && !item.serialized && van && !stock && <p className="mt-1 text-xs text-amber-700" data-testid={`tech-material-notinvan-${i}`}>Materiale non presente nel furgone: il magazzino riceverà un avviso.</p>}
    </div>
  );
};

export const TechMaterials = ({ list, setList, teamId }) => {
  const { van, catalog, frequent } = useVan(teamId);
  const update = (i) => (patch) => setList(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <section className="border bg-white p-4" data-testid="tech-materials">
      <div className="micro-label mb-3">Materiali utilizzati {van && <span className="normal-case tracking-normal text-muted-foreground">· scaricati da {van.name}</span>}</div>
      {list.map((m, i) => <Row key={i} m={m} i={i} van={van} catalog={catalog} frequent={frequent} update={update(i)} remove={() => setList(list.filter((_, j) => j !== i))} />)}
      <Button variant="outline" className="h-11 w-full" onClick={() => setList([...list, { item_id: null, free: false, name: "", qty: 1, serial: "" }])} data-testid="tech-material-add"><Plus className="mr-1 h-4 w-4" />Aggiungi materiale</Button>
    </section>
  );
};

const REASONS = { GUASTO: "Guasto", RECUPERATO: "Recuperato (cessazione)", SOSTITUITO: "Sostituito (upgrade)" };

const DeviceRow = ({ d, i, devices, catalog, taken, customerId, vanName, update, remove }) => {
  const stock = devices.find((s) => s.item_id === d.item_id);
  const owner = stock?.owners || {};
  const ok = (st, s) => !st.owners?.[s] || st.owners[s] === customerId;
  const usable = (stock?.serials || []).filter((s) => ok(stock, s));
  const free = usable.filter((s) => s === d.serial || !taken.has(s));
  const known = !d.serial || stock?.serials.includes(d.serial);
  const options = devices.map((s) => ({ ...s, extra: `${s.serials.filter((x) => ok(s, x)).length} utilizzabili` }));
  return (
    <div className="mb-3 border-b pb-3 last:border-b-0" data-testid={`tech-device-row-${i}`}>
      <div className="flex gap-2">
        <DeviceSelect value={d.item_id} devices={options} title={`Nel ${vanName || "furgone"}`} onChange={(v) => update({ item_id: v, serial: "" })} testId={`tech-device-item-${i}`} />
        <ItemScan catalog={catalog} devices={devices} testId={`tech-device-item-scan-${i}`}
          onFound={(r) => { const own = devices.find((s) => s.item_id === r.item_id)?.owners?.[r.serial]; update({ item_id: r.item_id, serial: r.serial && !(own && own !== customerId) ? r.serial : "", foreign: r.serial && own && own !== customerId ? r.serial : "" }); }} />
        <Button size="icon" variant="ghost" className="h-11 shrink-0" onClick={remove} aria-label="Rimuovi" data-testid={`tech-device-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {d.item_id && (
        <div className="mt-2 flex gap-2">
          <select className="h-11 w-full border bg-white px-2 font-mono text-sm" value={known ? d.serial : ""} onChange={(e) => update({ serial: e.target.value, foreign: "" })} data-testid={`tech-device-serial-${i}`}>
            <option value="">Numero di serie…</option>
            {free.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <SerialScanButton onDetected={(code) => update({ serial: owner[code] && owner[code] !== customerId ? "" : code, foreign: owner[code] && owner[code] !== customerId ? code : "" })} testId={`tech-device-scan-${i}`} />
        </div>
      )}
      {d.foreign && <p className="mt-1 text-xs font-semibold text-red-700" data-testid={`tech-device-serial-foreign-${i}`}>Il seriale {d.foreign} è di un'altra committente: non puoi usarlo per questo ordine.</p>}
      {!known && <p className="mt-1 text-xs text-amber-700" data-testid={`tech-device-serial-warning-${i}`}>Seriale {d.serial} non presente nel tuo furgone: verrà registrato e segnalato al magazzino.</p>}
    </div>
  );
};

export const TechDevices = ({ list, setList, teamId, customerId, optional }) => {
  const { van, catalog } = useVan(teamId);
  const devices = (van?.stock || []).filter((s) => catalog.find((c) => c.id === s.item_id)?.serialized);
  const update = (i) => (patch) => setList(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const taken = new Set(list.map((d) => d.serial).filter(Boolean));
  return (
    <section className="border bg-white p-4" data-testid="tech-devices">
      <div className="micro-label mb-3">Apparati installati{optional && " (facoltativo)"} {van && <span className="normal-case tracking-normal text-muted-foreground">· scaricati da {van.name}</span>}</div>
      {!devices.length && <p className="mb-3 text-xs text-muted-foreground" data-testid="tech-devices-empty">Nessun apparato nel furgone.</p>}
      {list.map((d, i) => <DeviceRow key={i} d={d} i={i} devices={devices} catalog={catalog} taken={taken} customerId={customerId} vanName={van?.name} update={update(i)} remove={() => setList(list.filter((_, j) => j !== i))} />)}
      <Button variant="outline" className="h-11 w-full" onClick={() => setList([...list, { item_id: "", serial: "" }])} data-testid="tech-device-add"><Plus className="mr-1 h-4 w-4" />Aggiungi apparato installato</Button>
    </section>
  );
};

export const devicesPayload = (list) => list.filter((d) => d.item_id && d.serial).map(({ item_id, serial }) => ({ item_id, name: "", qty: 1, serial }));

const ReturnRow = ({ r, i, catalog, update, remove }) => (
  <div className="mb-3 border-b pb-3 last:border-b-0" data-testid={`tech-return-row-${i}`}>
    <div className="flex gap-2">
      <DeviceSelect value={r.item_id} devices={catalog.filter((c) => c.serialized).map((c) => ({ ...c, item_id: c.id }))} title="Catalogo apparati"
        onChange={(v) => update({ item_id: v })} testId={`tech-return-item-${i}`} />
      <ItemScan catalog={catalog} testId={`tech-return-item-scan-${i}`} onFound={(r) => update({ item_id: r.item_id })} />
      <Button size="icon" variant="ghost" className="h-11 shrink-0" onClick={remove} aria-label="Rimuovi" data-testid={`tech-return-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
    <div className="mt-2 flex gap-2">
      <Input className="h-11 font-mono" placeholder="Numero di serie" value={r.serial} onChange={(e) => update({ serial: e.target.value.trim() })} data-testid={`tech-return-serial-${i}`} />
      <SerialScanButton onDetected={(code) => update({ serial: code })} testId={`tech-return-scan-${i}`} />
    </div>
    <select className="mt-2 h-11 w-full border bg-white px-2 text-sm" value={r.reason} onChange={(e) => update({ reason: e.target.value })} data-testid={`tech-return-reason-${i}`}>
      {Object.entries(REASONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
    <Input className="mt-2 h-11" placeholder="Note (es. difetto riscontrato)" value={r.notes} onChange={(e) => update({ notes: e.target.value })} data-testid={`tech-return-notes-${i}`} />
  </div>
);

export const TechReturns = ({ list, setList }) => {
  const { catalog } = useVan();
  const update = (i) => (patch) => setList(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <section className="border bg-white p-4" data-testid="tech-returns">
      <div className="micro-label mb-1">Apparati recuperati o guasti</div>
      <p className="mb-3 text-xs text-muted-foreground">Restano nel furgone come reso finché il magazzino non li riceve.</p>
      {list.map((r, i) => <ReturnRow key={i} r={r} i={i} catalog={catalog} update={update(i)} remove={() => setList(list.filter((_, j) => j !== i))} />)}
      <Button variant="outline" className="h-11 w-full" onClick={() => setList([...list, { item_id: "", serial: "", reason: "GUASTO", notes: "" }])} data-testid="tech-return-add"><Plus className="mr-1 h-4 w-4" />Aggiungi apparato rientrato</Button>
    </section>
  );
};

export const returnsPayload = (list) => list.filter((r) => r.item_id && r.serial).map(({ item_id, serial, reason, notes }) => ({ item_id, serial, reason, notes }));

export const materialsPayload = (list) => list
  .filter((m) => m.item_id || m.name)
  .map(({ item_id, name, qty, serial }) => ({ item_id: item_id || null, name: name || "", qty: Number(qty) > 0 ? Number(qty) : 1, serial: serial || "" }));
